import React, { useState, useEffect } from 'react';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CreditCard, Trash2, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

const AddPaymentMethodForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      // Get setup intent from backend
      const { data } = await axios.post(`${API}/payment-methods/setup-intent`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Confirm the setup intent with the card
      const result = await stripe.confirmCardSetup(data.client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (result.error) {
        toast.error(result.error.message);
      } else {
        toast.success('Payment method saved successfully!');
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('Error saving payment method:', error);
      toast.error('Failed to save payment method');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="border rounded-lg p-4 bg-gray-50">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={!stripe || loading} className="flex-1">
          {loading ? 'Saving...' : 'Save Card'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

const SavedPaymentMethods = ({ onSelectPaymentMethod, selectedPaymentMethodId, showAddNew = true }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setPaymentMethods([]);
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/payment-methods`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setPaymentMethods(response.data.payment_methods || []);
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
      // Don't show error toast on 401 or empty response
      if (error.response?.status !== 401) {
        // Silent fail - user just has no saved cards yet
      }
      setPaymentMethods([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const handleDelete = async (paymentMethodId) => {
    if (!window.confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    setDeletingId(paymentMethodId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/payment-methods/${paymentMethodId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Payment method removed');
      fetchPaymentMethods();
      if (selectedPaymentMethodId === paymentMethodId && onSelectPaymentMethod) {
        onSelectPaymentMethod(null);
      }
    } catch (error) {
      console.error('Failed to delete payment method:', error);
      toast.error('Failed to remove payment method');
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddSuccess = () => {
    setShowAddForm(false);
    fetchPaymentMethods();
  };

  const getCardBrandIcon = (brand) => {
    return brand.toUpperCase();
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-600">Loading saved cards...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {paymentMethods.length === 0 && !showAddForm ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
          <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">No saved payment methods</p>
          {showAddNew && (
            <Button onClick={() => setShowAddForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Payment Method
            </Button>
          )}
        </div>
      ) : (
        <>
          {paymentMethods.map((pm) => (
            <Card
              key={pm.id}
              className={`cursor-pointer transition ${
                selectedPaymentMethodId === pm.id
                  ? 'border-2 border-blue-600 bg-blue-50'
                  : 'hover:shadow-md'
              }`}
              onClick={() => onSelectPaymentMethod && onSelectPaymentMethod(pm.id)}
            >
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <CreditCard className="w-6 h-6 text-gray-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">
                        {getCardBrandIcon(pm.card.brand)} •••• {pm.card.last4}
                      </div>
                      <div className="text-sm text-gray-600">
                        Expires {pm.card.exp_month}/{pm.card.exp_year}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedPaymentMethodId === pm.id && (
                      <div className="bg-blue-600 text-white p-2 rounded-full">
                        <Check className="w-4 h-4" />
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(pm.id);
                      }}
                      disabled={deletingId === pm.id}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {showAddNew && !showAddForm && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowAddForm(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Payment Method
            </Button>
          )}
        </>
      )}

      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Add New Payment Method</CardTitle>
          </CardHeader>
          <CardContent>
            <Elements stripe={stripePromise}>
              <AddPaymentMethodForm
                onSuccess={handleAddSuccess}
                onCancel={() => setShowAddForm(false)}
              />
            </Elements>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SavedPaymentMethods;
